import { baseApi } from './baseApi';
import { encryptPayload, decryptPayload } from '../../utils/encryption.utils';
import {
  parseInitiateResponse,
  parseConfirmResponse,
  parseCustomerProfileResponse,
  parseOnboardingStatusResponse,
  parseCustomerErrorResponse,
  parseUpdateCustomerProfileResponse
} from '../parsers/customer.parsers';

/**
 * Conditional encryption based on environment variable
 */
const shouldEncrypt = import.meta.env.VITE_ENABLE_ENCRYPTION === 'true';

/**
 * Generate random UUID for requestId
 */
const generateRequestId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Generate mock JWT token
 */
const generateMockToken = () => {
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const payload = btoa(JSON.stringify({
    sub: 'customer_123',
    email: 'customer@example.com',
    exp: Math.floor(Date.now() / 1000) + 3600
  }));
  const signature = btoa('mock_signature');
  return `${header}.${payload}.${signature}`;
};

/**
 * Simulate network delay
 */
const delay = (ms = 800) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Customer API service
 * Extends the base API with customer management endpoints
 * 
 * @use Signup Flow
 * 1. Call initiateSignup with user details → get requestId
 * 2. Show OTP input screen
 * 3. Call confirmSignup with requestId → get token
 * 4. Store token and redirect to dashboard
 * 
 * @example
 * const [initiateSignup] = useInitiateSignupMutation();
 * const { data } = await initiateSignup({ name, email, mobile, deviceInfo, appInfo });
 */
export const customerApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Initiate signup process
     * POST /customer/signup/initiate
     * 
     * @purpose Start the customer signup flow by sending OTP to provided mobile/email
     * @useCase User registration, onboarding
     * 
     * @request {Object} payload
     * @request {string} payload.name - Customer full name
     * @request {string} payload.email - Customer email address
     * @request {string} payload.mobile - Customer mobile number
     * @request {Object} payload.deviceInfo - Device information
     * @request {Object} payload.appInfo - Application information
     * 
     * @response {Object}
     * @response {number} code - HTTP status code (200)
     * @response {string} message - Success message
     * @response {Object} result.requestId - Unique request identifier for OTP confirmation
     * 
     * @mock Returns random UUID as requestId after 800ms delay
     */
    initiateSignup: builder.mutation({
      async queryFn() {
        // Simulate network delay for mock
        await delay(Math.random() * 400 + 800);
        
        // Mock response - in production this would call baseQuery
        const mockResponse = {
          code: 200,
          message: 'OTP sent successfully',
          result: {
            requestId: generateRequestId()
          }
        };
        
        return { data: mockResponse };
      },
      transformResponse: (response) => {
        return parseInitiateResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseCustomerErrorResponse(error);
      },
    }),

    /**
     * Confirm signup and get auth token
     * POST /customer/signup/confirm
     * 
     * @purpose Verify OTP and complete signup process
     * @useCase Complete user registration after OTP verification
     * 
     * @request {Object} payload
     * @request {string} payload.requestId - Request ID from initiate signup
     * @request {string} payload.otp - OTP verification code
     * 
     * @response {Object}
     * @response {number} code - HTTP status code (200)
     * @response {string} message - Success message
     * @response {Object} result.token - JWT authentication token
     * 
     * @mock Returns mock JWT token after 600ms delay
     */
    confirmSignup: builder.mutation({
      query: (payload) => {
        const data = shouldEncrypt ? { encrypted: encryptPayload(payload) } : payload;
        return {
          url: '/customer/signup/confirm',
          method: 'POST',
          body: data,
          headers: {
            'Content-Type': 'application/json',
          },
        };
      },
      async queryFn() {
        // Simulate network delay
        await delay(Math.random() * 400 + 600);
        
        // Mock response
        const mockResponse = {
          data: {
            code: 200,
            message: 'Signup verified successfully',
            result: {
              token: generateMockToken()
            }
          }
        };
        
        return { data: mockResponse };
      },
      transformResponse: (response) => {
        const decrypted = shouldEncrypt && response?.encrypted
          ? decryptPayload(response.encrypted)
          : response;
        return parseConfirmResponse(decrypted);
      },
      transformErrorResponse: (error) => {
        const decrypted = shouldEncrypt && error?.data?.encrypted
          ? decryptPayload(error.data.encrypted)
          : error;
        return parseCustomerErrorResponse(decrypted);
      },
      invalidatesTags: ['Customer', 'User'],
    }),

    /**
     * Initiate login process
     * POST /customer/login/initiate
     * 
     * @purpose Start the customer login flow by sending OTP
     * @useCase User authentication, re-login
     * 
     * @request {Object} payload
     * @request {string} payload.username - Customer username (mobile or email)
     * 
     * @response {Object}
     * @response {number} code - HTTP status code (200)
     * @response {string} message - Success message
     * @response {Object} result.requestId - Unique request identifier for OTP confirmation
     * 
     * @mock Returns random UUID as requestId after 700ms delay
     */
    initiateLogin: builder.mutation({
      query: (payload) => {
        const data = shouldEncrypt ? { encrypted: encryptPayload(payload) } : payload;
        return {
          url: '/customer/login/initiate',
          method: 'POST',
          body: data,
          headers: {
            'Content-Type': 'application/json',
          },
        };
      },
      async queryFn() {
        // Simulate network delay
        await delay(Math.random() * 300 + 700);
        
        // Mock response
        const mockResponse = {
          data: {
            code: 200,
            message: 'OTP sent successfully',
            result: {
              requestId: generateRequestId()
            }
          }
        };
        
        return { data: mockResponse };
      },
      transformResponse: (response) => {
        const decrypted = shouldEncrypt && response?.encrypted
          ? decryptPayload(response.encrypted)
          : response;
        return parseInitiateResponse(decrypted);
      },
      transformErrorResponse: (error) => {
        const decrypted = shouldEncrypt && error?.data?.encrypted
          ? decryptPayload(error.data.encrypted)
          : error;
        return parseCustomerErrorResponse(decrypted);
      },
    }),

    /**
     * Confirm login and get auth token
     * POST /customer/login/confirm
     * 
     * @purpose Verify OTP and complete login process
     * @useCase Complete user authentication after OTP verification
     * 
     * @request {Object} payload
     * @request {string} payload.requestId - Request ID from initiate login
     * @request {string} payload.otp - OTP verification code
     * 
     * @response {Object}
     * @response {number} code - HTTP status code (200)
     * @response {string} message - Success message
     * @response {Object} result.token - JWT authentication token
     * 
     * @mock Returns mock JWT token after 600ms delay
     */
    confirmLogin: builder.mutation({
      query: (payload) => {
        const data = shouldEncrypt ? { encrypted: encryptPayload(payload) } : payload;
        return {
          url: '/customer/login/confirm',
          method: 'POST',
          body: data,
          headers: {
            'Content-Type': 'application/json',
          },
        };
      },
      async queryFn() {
        // Simulate network delay
        await delay(Math.random() * 400 + 600);
        
        // Mock response
        const mockResponse = {
          data: {
            code: 200,
            message: 'Login verified successfully',
            result: {
              token: generateMockToken()
            }
          }
        };
        
        return { data: mockResponse };
      },
      transformResponse: (response) => {
        const decrypted = shouldEncrypt && response?.encrypted
          ? decryptPayload(response.encrypted)
          : response;
        return parseConfirmResponse(decrypted);
      },
      transformErrorResponse: (error) => {
        const decrypted = shouldEncrypt && error?.data?.encrypted
          ? decryptPayload(error.data.encrypted)
          : error;
        return parseCustomerErrorResponse(decrypted);
      },
      invalidatesTags: ['Customer', 'User'],
    }),

    /**
     * Get customer profile information
     * GET /api/customer/profile
     * 
     * @purpose Retrieve authenticated customer's profile details
     * @useCase Display profile information, edit profile, KYC status check
     * 
     * @request {} - No request parameters (uses auth token from headers)
     * 
     * @response {Object}
     * @response {number} code - HTTP status code (200)
     * @response {string} message - Success message
     * @response {Object} result - Customer profile data
     * @response {Object} result.customer - Customer basic information
     * @response {Object} result.personalDetails - Personal details information
     * @response {Object} result.addressDetails - Address information
     * @response {Object} result.fatcaDetails - FATCA details
     * @response {Object} result.uccDetails - UCC details
     * @response {Object} result.kycDocuments - KYC documents
     */
    getCustomerProfile: builder.query({
      query: () => ({
        url: '/api/customer/profile',
        method: 'GET',
      }),
      transformResponse: (response) => {
        const decrypted = shouldEncrypt && response?.encrypted
          ? decryptPayload(response.encrypted)
          : response;
        return parseCustomerProfileResponse(decrypted);
      },
      transformErrorResponse: (error) => {
        const decrypted = shouldEncrypt && error?.data?.encrypted
          ? decryptPayload(error.data.encrypted)
          : error;
        return parseCustomerErrorResponse(decrypted);
      },
      providesTags: ['Customer'],
    }),

    /**
     * Update customer profile (partial)
     * PATCH /api/customer/profile
     *
     * @purpose Save FATCA fields after KYC complete (occupation, incomeSlab, birthPlace)
     * @request {Object} payload - Partial profile fields
     */
    updateCustomerProfile: builder.mutation({
      query: (payload) => ({
        url: '/api/customer/profile',
        method: 'PATCH',
        body: payload ?? {},
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response) => {
        const decrypted = shouldEncrypt && response?.encrypted
          ? decryptPayload(response?.encrypted)
          : response;
        return parseUpdateCustomerProfileResponse(decrypted ?? null);
      },
      transformErrorResponse: (error) => {
        const decrypted = shouldEncrypt && error?.data?.encrypted
          ? decryptPayload(error?.data?.encrypted)
          : error;
        return parseCustomerErrorResponse(decrypted ?? null);
      },
      invalidatesTags: ['Customer'],
    }),

    /**
     * Update the customer's display name
     * POST /api/customer/update_customer_name
     *
     * @purpose Let a customer correct the name shown on their profile
     * @request {Object} payload
     * @request {string} payload.name - New full name
     *
     * @response {Object}
     * @response {number} code - HTTP status code (200)
     * @response {string} message - Success message
     */
    updateCustomerName: builder.mutation({
      query: (payload) => ({
        url: '/api/customer/update_customer_name',
        method: 'POST',
        body: { name: payload?.name ?? '' },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response) => {
        const decrypted = shouldEncrypt && response?.encrypted
          ? decryptPayload(response?.encrypted)
          : response;
        return parseUpdateCustomerProfileResponse(decrypted ?? null);
      },
      transformErrorResponse: (error) => {
        const decrypted = shouldEncrypt && error?.data?.encrypted
          ? decryptPayload(error?.data?.encrypted)
          : error;
        return parseCustomerErrorResponse(decrypted ?? null);
      },
      // Refetches the profile so the header, tabs and Personal Details all
      // pick up the new name from one place.
      invalidatesTags: ['Customer', 'User'],
    }),

    /**
     * Get customer onboarding status
     * GET /api/customer/onboarding-status
     * 
     * @purpose Retrieve customer's onboarding completion status
     * @useCase Check account setup progress, determine which steps are completed
     * 
     * @request {} - No request parameters (uses auth token from headers)
     * 
     * @response {Object}
     * @response {number} code - HTTP status code (200)
     * @response {string} message - Success message
     * @response {Object} result - Onboarding status data
     * 
     * @mock Returns onboarding status after 500ms delay
     */
    getOnboardingStatus: builder.query({
      query: () => ({
        url: '/api/customer/onboarding-status',
        method: 'GET',
      }),
      // async queryFn() {
      //   // Simulate network delay
      //   await delay(Math.random() * 200 + 500);
        
      //   // Mock response with onboarding status data
      //   const mockResponse = {
      //     data: {
      //       code: 200,
      //       message: 'Onboarding status retrieved successfully',
      //       result: {
      //         kycCompleted: true,
      //         fatcaCompleted: true,
      //         bankAccountLinked: false,
      //         nomineeCompleted: false,
      //         overallStatus: 'in_progress'
      //       }
      //     }
      //   };
        
      //   return { data: mockResponse };
      // },
      transformResponse: (response) => {
        return parseOnboardingStatusResponse(response);
      },
      transformErrorResponse: (error) => {
        return parseCustomerErrorResponse(error);
      },
      providesTags: ['Customer'],
    }),

    /**
     * Get Volume Drivers and Sector Data
     * GET /mutual-funds/volume-drivers
     */
    getVolumeDrivers: builder.query({
      query: () => ({
        url: 'api/mutual-funds/volume-drivers',
        method: 'POST',
        body: {}
      }),
      transformErrorResponse: (error) => {
        return parseCustomerErrorResponse(error);
      },
    }),

    /**
     * Get holding activity
     * GET /api/holdings/activity/{id}
     */
    getHoldingActivity: builder.query({
      query: ({ id, page = 1, limit = 10 }) => ({
        url: `/api/holdings/activity/${id}`,
        method: 'GET',
        params: { page, limit }
      }),
      transformErrorResponse: (error) => {
        return parseCustomerErrorResponse(error);
      },
    }),

    /**
     * Get holding order history
     * GET /api/holdings/order-history/{id}
     */
    getHoldingOrderHistory: builder.query({
      query: ({ id, page = 1, limit = 10 }) => ({
        url: `/api/holdings/order-history/${id}`,
        method: 'GET',
        params: { page, limit }
      }),
      transformErrorResponse: (error) => {
        return parseCustomerErrorResponse(error);
      },
    }),

    /**
     * Deactivate customer account
     * POST /deactivate-account
     * 
     * @purpose Permanently deactivate the customer's account
     * @useCase User requests account closure/deactivation
     * 
     * @request {} - No request parameters (uses auth token from headers)
     * 
     * @response {Object}
     * @response {number} code - HTTP status code
     * @response {string} message - Success/Error message
     */
    deactivateAccount: builder.mutation({
      query: () => ({
        url: '/api/customer/deactivate-account',
        method: 'POST',
      }),
      // transformErrorResponse: (error) => {
      //   return parseCustomerErrorResponse(error);
      // },
      invalidatesTags: ['Customer', 'User'],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useInitiateSignupMutation,
  useConfirmSignupMutation,
  useInitiateLoginMutation,
  useConfirmLoginMutation,
  useGetCustomerProfileQuery,
  useUpdateCustomerProfileMutation,
  useUpdateCustomerNameMutation,
  useGetOnboardingStatusQuery,
  useLazyGetOnboardingStatusQuery,
  useDeactivateAccountMutation,
  useGetHoldingActivityQuery,
  useGetHoldingOrderHistoryQuery,
  useGetVolumeDriversQuery
} = customerApi;

